import MenuIcon from '@mui/icons-material/Menu';
import { useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import RectangularButton from '~/Components/RectangularButton/RectangularButton';
import { BORDER_COLOR, GRAY_LIGHT_2, PRIMARY_BLUE, WHITE } from '~/Constants/Colors.constants';
import {
  DRAWER_WIDTH,
  FONT_SIZE_SMALLISH,
  HEADER_HEIGHT_DESKTOP,
  HEADER_HEIGHT_MOBILE,
} from '~/Constants/UI.constants';
import { useOrganisation, useProvider, useUser } from '~/Hooks';
import { getUserFullName } from '~/Models/DTO/User.model';
import { AppBar, Button, ProfilePictureCard, Toolbar, Typography } from '~/UIKit';
import { makeStyles } from '~/UIKit/Style';
import { useHeader } from '.';
import { AccountMenuItems } from './Menu';

const useStyles = makeStyles(
  (theme) => ({
    menuButton: {
      fontSize: FONT_SIZE_SMALLISH,
      marginRight: theme.spacing(2),
      color: PRIMARY_BLUE,
      border: 'none !important',
    },
    appBar: {
      zIndex: 1100, // Lower than drawer
      borderBottom: `thin solid ${BORDER_COLOR}`,
      backgroundColor: WHITE,
      width: `calc(100% - ${DRAWER_WIDTH}px)`,
      marginLeft: DRAWER_WIDTH,
      [theme.breakpoints.down('lg')]: {
        width: '100%',
        marginLeft: 0,
      },
    },
    headerUserMenu: {
      minWidth: `calc(${DRAWER_WIDTH}px - 17px)`,
      display: 'flex',
      alignItems: 'center',
    },
    menuButtonIcon: {
      height: 25,
      width: 25,
      fontWeight: 'bold',
      marginRight: 5,
    },
    searchButtonIcon: {
      marginLeft: 'auto',
      color: WHITE,
    },
    toolBar: {
      padding: '0 16px',
      minHeight: HEADER_HEIGHT_DESKTOP - 1,
      justifyContent: 'space-between',
      [theme.breakpoints.down('md')]: {
        minHeight: HEADER_HEIGHT_MOBILE + 2,
        padding: '0 8px',
      },
    },
    headerName: {
      fontWeight: 'bold',
      fontSize: 17,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: 200,
      color: GRAY_LIGHT_2,
      [theme.breakpoints.down('md')]: {
        maxWidth: 150,
      },
    },
    rightSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      marginLeft: 'auto',
    },
    searchContainer: {
      marginRight: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        marginRight: 0,
      },
    },
  }),
  { name: 'Header' },
);

export interface IHeaderProps {
  openDrawer: () => void;
}

export const Header = ({ openDrawer }: IHeaderProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { currentUser } = useUser();
  const { currentProvider } = useProvider();
  const { organisation } = useOrganisation();
  const {
    logout,
    handleNavigation,
    handleProfileMenuOpen,
    handleMenuClose,
    anchorEl,
  } = useHeader();

  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const handleNavigate = (path: string) => {
    handleNavigation(path);
    handleMenuClose();
  };

  return (
    <AppBar className={classes.appBar} color="primary" elevation={0} position="relative">
      <Toolbar className={classes.toolBar} disableGutters>
        {/* Tablet only */}
        {isTablet && (
          <>
            <RectangularButton
              icon={<MenuIcon className={classes.menuButtonIcon} />}
              onClick={openDrawer}
              data-testid="main.menu"
              className={classes.headerMenu}
            >
              {t('app.menu')}
            </RectangularButton>
          </>
        )}

        {/* Desktop only */}
        {!isTablet && (
          <div className={classes.headerUserMenu}>
            <Typography className={classes.headerName}>
              {organisation?.name || currentProvider?.name || ''}
            </Typography>
          </div>
        )}

        <div className={classes.rightSection}>
          <Button onClick={handleProfileMenuOpen} data-testid="main.userMenu">
            <ProfilePictureCard
              imageUrl={currentUser?.image_url}
              displayName={getUserFullName(currentUser) || ''}
              hasArrowDown
              responsiveLabel
              maxWidth={150}
            />
          </Button>
        </div>
      </Toolbar>
      <AccountMenuItems
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        onLogout={logout}
        onNavigate={handleNavigate}
      />
    </AppBar>
  );
};
